import React, { FC, useContext } from 'react';
import { ascComparator, dataURLtoFile, T, uploadImage } from 'src/shared/helpers';
import styled from 'styled-components';

import { Checkbox } from '../../../../../../widgets/checkbox';
import { MessageDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { useProductConfigurationState } from 'src/components/pages/productConfiguration/state';
import usePreview3DStore, { Preview3DStoreContext } from 'src/components/widgets/preview3D/store';
import Screenshot from 'src/shared/material_icons/image/photo_camera.svg';
import Merge from 'src/shared/material_icons/action/view_column.svg';
import Copy from 'src/shared/material_icons/content/content_copy.svg';
import Edit from 'src/shared/material_icons/image/edit.svg';
import Retarget from 'src/shared/material_icons/action/open_in_full.svg';
import Import from 'src/shared/material_icons/file/upload_file.svg';
import Link from 'src/shared/material_icons/content/link.svg';
import Sparkles from 'src/components/widgets/svg/sparkles';
import Plus from 'src/shared/material_icons/content/add.svg';
import Trash from 'src/shared/material_icons/action/delete.svg';
import theme from 'src/shared/theme';
import { ComposerAttributeSubTreeElement, ComposerAttributeTreeElement } from '../../../configurator-composer/composer/components/composerAttributeTreeElement';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import { ComposerCopyOptionsAttributeDialog } from '../../../configurator-composer/composer/dialogs/composerCopyOptionsAttributeDialog';
import ComposerGenerateAIAttributeDialog from '../../../configurator-composer/composer/dialogs/composerGenerateAIAttributeDialog';
import ComposerImportAttributeDialog from '../../../configurator-composer/composer/dialogs/composerImportAttributeDialog';
import ComposerLinkAttributeDialog from '../../../configurator-composer/composer/dialogs/composerLinkAttributeDialog';
import { ComposerMergeAttributeDialog } from '../../../configurator-composer/composer/dialogs/composerMergeAttributeDialog';
import ComposerRetargetAttributeDialog from '../../../configurator-composer/composer/dialogs/composerRetargetAttributeDialog';
import ComposerScreenshotAttributeDialog, { ComposerScreenshotType } from '../../../configurator-composer/composer/dialogs/composerScreenshotAttributeDialog';
import ComposerSaveAttributeDialog from '../../../configurator-composer/composer/dialogs/save/composerSaveAttributeDialog';
import {
    getRetargetObjectsAndMaterials,
    randomCode,
    retargetMapToActions,
    sortByDisplay
} from '../../../configurator-composer/composer/helpers/composerHelpers';
import { ComposerAttributeNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';
import { TreeNodeHook } from '../../scene-editors/hooks/useTreeNodeManager';
import { ToolGrid } from '../../toolsgrid';
import { ComposerAttributeNode, ComposerAttributeOptionNode, ComposerAttributeType } from './composerAttributesModels';

const ComposerAttributesContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
`;

const ComposerAttributesHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: start;
    border-bottom: 1px solid ${theme.app.borderColor};
    min-height: 160px;
`;

const ComposerAttributesBody = styled.div`
    padding: 10px 0;
    flex: 1;
    overflow-y: auto;
`;

const ComposerAttributesList = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
    margin: 0;
    height: 100%;
    overflow: hidden;
    padding-top: 30px;
    height: calc(100% - 30px);
    overflow-y: auto;
`;

const ComposerAttributesOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: transparent;
`;

const ComposerAttributeDeselectorContainer = styled.div`
    position: absolute;
    height: 30px;
    top: 0;
    left: 0;
    z-index: 10;
    cursor: pointer;
`;
const ComposerAttributeCollapseContainer = styled.div`
    position: absolute;
    height: 30px;
    top: 0;
    right: 0;
    font-size: 12px;
    text-align: right;
    z-index: 9;
    cursor: pointer;
`;

const ComposerAttributesSelector: FC<{ selected: boolean; onClick: () => void }> = ({ selected, onClick }) => {
    return (
        <ComposerAttributeDeselectorContainer onClick={onClick}>
            <Checkbox checked={selected} onChange={onClick} label='Select all items' />
        </ComposerAttributeDeselectorContainer>
    );
};

const ComposerAttributesCollapseSelector: FC<{ onExpand: () => void; onCollapse: () => void }> = ({ onExpand, onCollapse }) => {
    return (
        <ComposerAttributeCollapseContainer>
            <span onClick={onExpand} style={{ marginRight: '10px' }}>
                {T._('Expand All', 'Admin')}
            </span>
            <span onClick={onCollapse}>{T._('Collapse All', 'Admin')}</span>
        </ComposerAttributeCollapseContainer>
    );
};

const ComposerAttributes: FC<{ state: TreeNodeHook<ComposerAttributeNode> }> = ({ state }) => {
    const [
        modelAttributes,
        storedEcommerceSettings,
        currentAttributes,
        currentSteps,
        currentGroups,
        selectedOptions,
        storeMeshList,
        storeMaterialsList,
        selectAttributes,
        updateAttribute,
        removeAttributes,
        cloneAttributes,
        updateOption,
        validateAttributes,
        selectOptions
    ] = useComposerStore((state) => [
        state.modelAttributes,
        state.ecommerceSettings,
        state.attributes,
        state.attributesSteps,
        state.attributesGroups,
        state.selectedOptions,
        state.sceneObjectList,
        state.sceneMaterialList,
        state.selectAttributes,
        state.updateAttribute,
        state.removeAttributes,
        state.cloneAttributes,
        state.updateOption,
        state.validateAttributes,
        state.selectOptions
    ]);
    const [zkScene, sceneEditor, zkProduct] = usePreview3DStore((x) => [x.zkScene, x.sceneEditor, x.zkProduct]);

    const preview3DStore = useContext(Preview3DStoreContext);
    const { state: productState } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();

    const { filter, setFilter, selectedIds, filteredData, updateNode, findNodeById, removeNodes, treeState, setSelectedIds } = state;

    const storeSelectedAttributes = useComposerStore((state) => state.selectedAttributes);
    React.useEffect(() => {
        if (storeSelectedAttributes && storeSelectedAttributes.length > 0) {
            const attributeIds = storeSelectedAttributes.map((attr) => attr.id);
            const currentIds = [...selectedIds].sort().join(',');
            const storeIds = [...attributeIds].sort().join(',');

            if (currentIds !== storeIds) {
                console.log('Syncing attribute selectedIds with store | store has:', attributeIds.length, '| local has:', selectedIds.length);
                // Use queueMicrotask to ensure this happens after tree reinit
                queueMicrotask(() => {
                    setSelectedIds(attributeIds);
                });
            }
        } else if (selectedIds.length > 0 && (!storeSelectedAttributes || storeSelectedAttributes.length === 0)) {
            // Store has no selection, clear local selection
            setSelectedIds([]);
        }
    }, [storeSelectedAttributes, currentAttributes, selectedIds]); // Re-sync when attributes change (tree reinit)

    const selectedAttributes = React.useMemo(() => {
        if (!selectedIds || selectedIds.length === 0) return [];
        return selectedIds.map((id) => findNodeById(id)).filter((x) => x) as ComposerAttributeNode[];
    }, [selectedIds, findNodeById]);
    const oneAttributeSelected = selectedIds && selectedIds.length === 1;
    const attributeSelected = selectedAttributes?.[0] ?? null;
    const sceneMeshList = storeMeshList.filter((x) => sceneEditor!.isRealMesh(x)).sort(ascComparator((item) => item.name ?? '')) ?? [];
    const sceneMaterialList =
        storeMaterialsList.filter((x) => x.id != 'default material' && x.name != 'default material').sort(ascComparator((item) => item.name ?? '')) ?? [];
    const buttons = [
        { icon: <Plus />, name: T._('Add', 'Admin'), tooltip: T._('Add Attribute', 'Admin'), onClick: () => onAttributeButtonClick('add') },
        {
            icon: <Sparkles />,
            name: T._('Add with AI', 'Admin'),
            tooltip: T._('Add Attribute with AI', 'Admin'),
            onClick: () => onAttributeButtonClick('add-ai')
        },
        {
            icon: <Edit />,
            name: T._('Edit', 'Admin'),
            tooltip: T._('Edit Attribute', 'Admin'),
            disabled: !oneAttributeSelected,
            onClick: () => onAttributeButtonClick('edit')
        },
        {
            icon: <Trash />,
            name: T._('Remove', 'Admin'),
            tooltip: T._('Remove Attribute', 'Admin'),
            disabled: !selectedAttributes || selectedAttributes.length === 0,
            onClick: () => onAttributeButtonClick('remove')
        },
        {
            icon: <Copy />,
            name: T._('Duplicate', 'Admin'),
            tooltip: T._('Duplicate Attribute', 'Admin'),
            disabled: !selectedAttributes || selectedAttributes.length === 0,
            onClick: () => onAttributeButtonClick('duplicate')
        },
        {
            icon: <Screenshot />,
            name: T._('Screenshot', 'Admin'),
            tooltip: T._('Take Screenshot', 'Admin'),
            onClick: () => onAttributeButtonClick('screenshot')
        },
        {
            icon: <Link />,
            name: T._('Link', 'Admin'),
            label: T._('Link', 'Admin'),
            tooltip: T._('Link Attribute', 'Admin'),
            disabled: !oneAttributeSelected || attributeSelected?.type !== ComposerAttributeType.Attribute,
            onClick: () => onAttributeButtonClick('link')
        },
        { icon: <Import />, name: T._('Import', 'Admin'), tooltip: T._('Import Attribute', 'Admin'), onClick: () => onAttributeButtonClick('import') },
        {
            icon: <Retarget />,
            name: T._('Retarget', 'Admin'),
            tooltip: T._('Retarget Attribute', 'Admin'),
            disabled: !oneAttributeSelected || (attributeSelected?.options?.length ?? 0) === 0,
            onClick: () => onAttributeButtonClick('retarget')
        },
        {
            icon: <Merge />,
            name: T._('Merge', 'Admin'),
            tooltip: T._('Merge Attributes', 'Admin'),
            disabled: currentAttributes.filter((attr) => attr.type === ComposerAttributeType.Attribute).length < 2,
            onClick: () => onAttributeButtonClick('merge')
        },
        {
            icon: <Copy />,
            name: T._('Copy', 'Admin'),
            tooltip: T._('Copy Attribute', 'Admin'),
            disabled: currentAttributes.filter((attr) => attr.type === ComposerAttributeType.Attribute).length < 2,
            onClick: () => onAttributeButtonClick('copy')
        }
    ];

    const searchOptions = (optionFilter: string, attributeFilter: string) => {
        let options: ComposerAttributeOptionNode[] = [];
        const attributeRegex = new RegExp('^' + attributeFilter.toLowerCase().replace(/\*/g, '.*') + '$');
        const optionRegex = new RegExp('^' + optionFilter.toLowerCase().replace(/\*/g, '.*') + '$');

        filteredData.forEach((attr) => {
            const name = attr.name?.toLowerCase() || '';
            if (!name.match(attributeRegex)) return;

            attr.options.forEach((opt) => {
                const name = opt.name?.toLowerCase() || '';
                if (name.match(optionRegex)) {
                    options.push(opt);
                }
            });
        });
        return options;
    };

    const editAttributeHandler = (attribute: ComposerAttributeNode) => {
        showDialog(
            'composerEditAttribute',
            <ComposerSaveAttributeDialog
                onConfirm={(updatedAttribute) => {
                    if (!updatedAttribute) return;
                    updatedAttribute.isNew = true;
                    updateAttribute(updatedAttribute);
                    validateAttributes();
                    closeDialog('composerEditAttribute');
                }}
                productCode={productState.product.code}
                salesChannelID={productState.salesChannelID}
                sceneCameraList={zkScene?.get?.('cameraLocations')?.models?.sort(ascComparator((item) => item.get('name') ?? '')) ?? []}
                meshList={storeMeshList?.filter((x) => sceneEditor?.isRealMesh(x)).sort(ascComparator((item) => item.name ?? '')) ?? []}
                edit={true}
                passedAttribute={attribute}
                fixedType={attribute.type}
                selectedPlatformAttribute={attribute.platformAttribute}
                ecommerceSettings={storedEcommerceSettings || null}
            />
        );
    };

    const onAttributeButtonClick = (action: string) => {
        switch (action) {
            case 'add':
                showDialog(
                    'composerCreateAttribute',
                    <ComposerSaveAttributeDialog
                        onConfirm={(attribute) => {
                            closeDialog('composerCreateAttribute');
                            if (!attribute) return;
                            attribute.isNew = true;
                            attribute.displayOrder = -1;
                            updateAttribute(attribute);
                            validateAttributes();
                            setSelectedIds([attribute.id]);
                            selectAttributes([attribute]);
                        }}
                        productCode={productState.product.code}
                        salesChannelID={productState.salesChannelID}
                        sceneCameraList={zkScene?.get?.('cameraLocations')?.models?.sort(ascComparator((item) => item.get('name') ?? '')) ?? []}
                        meshList={storeMeshList?.filter((x) => sceneEditor?.isRealMesh(x)).sort(ascComparator((item) => item.name ?? '')) ?? []}
                        edit={false}
                        passedAttribute={null}
                        selectedPlatformAttribute={null}
                        ecommerceSettings={storedEcommerceSettings || null}
                    />
                );
                break;
            case 'add-ai':
                showDialog(
                    'composerGenerateAIAttribute',
                    <Preview3DStoreContext.Provider value={preview3DStore}>
                        <ComposerGenerateAIAttributeDialog
                            onClose={() => closeDialog('composerGenerateAIAttribute')}
                            materialList={sceneMaterialList}
                            meshList={sceneMeshList}
                            sceneEditor={preview3DStore?.getState().sceneEditor}
                            onConfirm={(attributes) => {
                                closeDialog('composerGenerateAIAttribute');
                                if (!attributes) return;
                                attributes.forEach((attribute) => {
                                    attribute.isNew = true;
                                    updateAttribute(attribute);
                                });
                            }}
                        />
                    </Preview3DStoreContext.Provider>
                );
                break;
            case 'edit':
                if (!selectedAttributes || selectedAttributes.length === 0) {
                    console.warn('No attribute selected for editing');
                    return;
                }
                editAttributeHandler(selectedAttributes[0]);
                break;
            case 'remove':
                //confirm dialog
                if (!selectedAttributes || selectedAttributes.length === 0) {
                    console.warn('No attribute selected for removal');
                    return;
                }
                showDialog(
                    'confirmDialog',
                    <MessageDialog
                        title={T._('Confirm Removal', 'Admin')}
                        message={
                            T._('Are you sure you want to remove the selected attribute(s)? ', 'Admin') + selectedAttributes.map((attr) => attr.name).join(', ')
                        }
                        buttons={[
                            { label: T._('Cancel', 'Admin'), outline: true, onClick: () => closeDialog('confirmDialog') },
                            {
                                label: T._('Remove', 'Admin'),
                                destructive: true,
                                onClick: () => {
                                    closeDialog('confirmDialog');
                                    const ids = selectedAttributes.map((attr) => attr.id);
                                    const childIds = selectedAttributes.flatMap((attr) => getNodeChildren(attr)).map((child) => child.id);
                                    removeNodes(childIds);
                                    removeAttributes(childIds);
                                    removeNodes(ids);
                                    removeAttributes(ids);
                                    validateAttributes();
                                    setSelectedIds([]);
                                    selectAttributes(null);
                                }
                            }
                        ]}
                    />
                );
                break;
            case 'duplicate':
                if (!selectedAttributes || selectedAttributes.length === 0) {
                    console.warn('No attribute selected for duplication');
                    return;
                }
                const attributesToClone = [...selectedAttributes];
                const descendantsToClone: ComposerAttributeNode[] = [];

                const findDescendants = (node: ComposerAttributeNode) => {
                    const children = getNodeChildren(node);
                    for (const child of children) {
                        descendantsToClone.push(child);
                        findDescendants(child);
                    }
                };

                selectedAttributes.forEach(findDescendants);

                const allToClone = [...descendantsToClone, ...attributesToClone];
                const clonedAttributes = cloneAttributes(allToClone);
                validateAttributes();
                // setSelectedIds(clonedAttributes.map((attr) => attr.id));
                // setSelectedIds(clonedAttributes.map((attr) => attr.id));
                selectAttributes(clonedAttributes);
                break;
            case 'screenshot':
                showDialog(
                    'screenshotDialog',
                    <ComposerScreenshotAttributeDialog
                        materialsList={sceneMaterialList}
                        onConfirm={async (result) => {
                            closeDialog('screenshotDialog');
                            let image = '';
                            let url = '';
                            const materialID = result.material ?? '';
                            if (result.type === ComposerScreenshotType.Upload) {
                                url = result.upload || '';
                            } else {
                                if (result.type === ComposerScreenshotType.Material) {
                                    image = (await sceneEditor?.getMaterialScreenshotAsync(materialID, 512, 512)) || '';
                                } else if (result.type === ComposerScreenshotType.Object) {
                                    image = (await sceneEditor?.getSceneContentScreenshotAsync(512, 512)) || '';
                                }
                                const response = await uploadImage(dataURLtoFile(image, 'preview-image'));
                                url = response;
                            }
                            for (const option of searchOptions(result.optionsFilter, result.attributeFilter)) {
                                const updatedOption: ComposerAttributeOptionNode = { ...option, img: url };
                                updateOption(updatedOption);
                            }
                            validateAttributes();
                        }}
                    />
                );
                break;
            case 'link':
                const currentAttribute = selectedAttributes?.[0] || null;
                if ((selectedAttributes && selectedAttributes?.length > 1) || currentAttribute?.type !== ComposerAttributeType.Attribute) {
                    console.warn('Linking attributes is only available for a single selected attribute of type "Attribute"');
                    return;
                }
                showDialog(
                    'composerLinkAttributeDialog',
                    <ComposerLinkAttributeDialog
                        currentAttribute={currentAttribute}
                        modelAttributes={filteredData || []}
                        onConfirm={(result) => {
                            closeDialog('composerLinkAttributeDialog');
                            if (!result || !result.links) return;
                            const filteredData = result.links
                                .filter((l) => l.attributeID && l.optionID)
                                .map((l) => ({
                                    attributeID: l.attributeID,
                                    operation: l.comparisonOperator,
                                    optionID: l.optionID
                                }));
                            const updatedAttribute: ComposerAttributeNode = {
                                ...currentAttribute,
                                linkOperation: result.logicalOperator,
                                links: filteredData
                            };
                            updateAttribute(updatedAttribute);
                            validateAttributes();
                        }}
                    />
                );
                break;
            case 'import':
                showDialog(
                    'composerImportAttribute',
                    <ComposerImportAttributeDialog
                        salesChannelID={productState.salesChannelID}
                        materialsList={sceneMaterialList}
                        objectsList={sceneMeshList}
                        onConfirm={(result) => {
                            closeDialog('composerImportAttribute');
                            if (!result || !result.selectedAttributes || result.selectedAttributes.length === 0) {
                                return;
                            }
                            const imported: ComposerAttributeNode[] = [];
                            for (const srcAttr of result.selectedAttributes) {
                                // Clone the attribute with new IDs
                                const newAttr: ComposerAttributeNode = {
                                    ...srcAttr,
                                    id: MPlaza.generateUUID(),
                                    idNumeric: -1,
                                    code: randomCode(srcAttr.code || 'attribute'),
                                    isNew: true,
                                    displayOrder: -1,
                                    parentID: undefined,
                                    parentIDNumeric: -1,
                                    parentType: undefined,
                                    links: [],
                                    linkOperation: undefined,
                                    options: srcAttr.options.map((opt, idx) => {
                                        const clonedOpt = {
                                            ...opt,
                                            id: MPlaza.generateUUID(),
                                            idNumeric: -1,
                                            attributeID: '', // Will be set below
                                            links: [],
                                            linkOperation: undefined,
                                            displayOrder: idx,
                                            actions: (opt.actions || []).map((action) => ({
                                                ...action,
                                                id: MPlaza.generateUUID(),
                                                attributeID: '',
                                                optionID: ''
                                            }))
                                        };
                                        return clonedOpt;
                                    })
                                };

                                // Fix references after generating the new attribute ID
                                newAttr.options = newAttr.options.map((opt) => ({
                                    ...opt,
                                    attributeID: newAttr.id,
                                    actions: opt.actions.map((a) => ({
                                        ...a,
                                        attributeID: newAttr.id,
                                        optionID: opt.id
                                    }))
                                }));

                                // Remap actions using object/material matches
                                const allActions = newAttr.options.flatMap((opt) => opt.actions || []);
                                const remapped = retargetMapToActions(allActions, result.materialMatches, result.objectMatches);
                                newAttr.options.forEach((opt) => {
                                    opt.actions = (opt.actions || []).map((a) => {
                                        const found = remapped.find((r) => r.id === a.id);
                                        return found || a;
                                    });
                                });

                                updateAttribute(newAttr);
                                imported.push(newAttr);
                            }
                            validateAttributes();
                            setSelectedIds(imported.map((a) => a.id));
                            selectAttributes(imported);
                        }}
                    />
                );
                break;
            case 'retarget':
                const currentAttr = selectedAttributes?.[0] || null;
                if (!currentAttr) {
                    console.warn('Retargeting attributes is only available for a single selected attribute');
                    return;
                }
                const options = currentAttr.options || [];
                if (options.length === 0) {
                    console.warn('Selected attribute has no options to retarget');
                    return;
                }
                const actions = options.flatMap((opt) => opt.actions || []);
                const { objectIds, materialIds } = getRetargetObjectsAndMaterials(actions);
                showDialog(
                    'composerRetargetAttribute',
                    <ComposerRetargetAttributeDialog
                        materialsList={sceneMaterialList}
                        objectsList={sceneMeshList}
                        usedMaterialsList={sceneMaterialList.filter((m) => materialIds.includes(m.id))}
                        usedObjectsList={sceneMeshList.filter((o) => objectIds.includes(o.id))}
                        onConfirm={(data) => {
                            if (!data) {
                                closeDialog('composerRetargetAttribute');
                                return;
                            }
                            closeDialog('composerRetargetAttribute');
                            const { materialMap, objectMap } = data;
                            retargetMapToActions(actions, materialMap, objectMap).forEach((action) => {
                                options.forEach((opt) => {
                                    const actionIndex = opt.actions?.findIndex((a) => a.id === action.id) ?? -1;
                                    if (actionIndex >= 0) {
                                        opt.actions![actionIndex] = action;
                                    }
                                });
                            });
                            currentAttr.options = options;
                            console.log('Retarget result', objectIds, materialIds, options);
                            updateAttribute(currentAttr);
                        }}
                    />
                );
                break;
            case 'merge':
                const attributes = currentAttributes.filter((attr) => attr.type === ComposerAttributeType.Attribute);
                if (attributes.length < 2) {
                    return;
                }
                showDialog(
                    'composerMergeAttributes',
                    <ComposerMergeAttributeDialog
                        attributesList={attributes}
                        onConfirm={(result) => {
                            closeDialog('composerMergeAttributes');
                            if (!result) return;
                            const { newAttribute, deleteSourceAttributes, attributesToMerge } = result;
                            if (!newAttribute || !attributesToMerge || attributesToMerge.length < 2) return;
                            if (!newAttribute.name.trim() || !newAttribute.code.trim()) return;

                            // Flatten and clone options from all selected attributes
                            const allOptions = attributesToMerge.flatMap((attr) => attr.options);
                            const optionCodeCounts = new Map<string, number>();
                            const mergedOptions: ComposerAttributeOptionNode[] = allOptions.map((opt, index) => {
                                // Deduplicate option codes with _1, _2 suffixes
                                let code = opt.code;
                                const count = optionCodeCounts.get(code) || 0;
                                if (count > 0) {
                                    code = `${opt.code}_${count}`;
                                }
                                optionCodeCounts.set(opt.code, count + 1);

                                return {
                                    ...opt,
                                    id: MPlaza.generateUUID(),
                                    idNumeric: -1,
                                    code,
                                    attributeID: newAttribute.id,
                                    isDefault: index === 0,
                                    displayOrder: index,
                                    links: [],
                                    linkOperation: undefined
                                };
                            });

                            newAttribute.options = mergedOptions;
                            newAttribute.isNew = true;
                            newAttribute.displayOrder = -1;
                            updateAttribute(newAttribute);
                            if (deleteSourceAttributes && attributesToMerge && attributesToMerge.length > 0) {
                                const childIds = attributesToMerge.flatMap((attr) => getNodeChildren(attr)).map((child) => child.id);
                                removeNodes(childIds);
                                removeAttributes(childIds);
                            }
                            validateAttributes();
                            setSelectedIds([newAttribute.id]);
                            selectAttributes([newAttribute]);
                        }}
                    />
                );
                break;
            case 'copy':
                showDialog(
                    'composerCopyAttribute',
                    <ComposerCopyOptionsAttributeDialog
                        attributesList={currentAttributes.filter((attr) => attr.type === ComposerAttributeType.Attribute)}
                        onConfirm={(result) => {
                            closeDialog('composerCopyAttribute');
                            if (!result) return;
                            const { optionsToCopy, targetAttribute, replaceExistingOptions } = result;
                            if (!targetAttribute || !optionsToCopy || optionsToCopy.length === 0) return;
                            const updatedAttribute: ComposerAttributeNode = { ...targetAttribute };
                            if (replaceExistingOptions) {
                                updatedAttribute.options = optionsToCopy;
                            } else {
                                const newOptions = [...(targetAttribute.options || [])];
                                optionsToCopy.forEach((opt) => {
                                    if (!newOptions.find((o) => o.id === opt.id)) {
                                        newOptions.push(opt);
                                    }
                                });
                                updatedAttribute.options = newOptions;
                            }
                            updateAttribute(updatedAttribute);
                            updateNode(updatedAttribute.id, updatedAttribute);
                            validateAttributes();
                            setSelectedIds([updatedAttribute.id]);
                            selectAttributes([updatedAttribute]);
                        }}
                    />
                );
                break;
            default:
                console.warn('Unknown action:', action);
        }
    };
    const parentNodes = React.useMemo(() => filteredData.filter((a) => (a.isNew && !a.parentID) || !a.parentID), [filteredData]);
    const nodes = React.useMemo(() => parentNodes.sort((a, b) => sortByDisplay(parentNodes, a, b)), [parentNodes]);

    const getNodeChildren = (node: ComposerAttributeNode) => {
        return filteredData.filter((attr) => attr.parentID === node.id);
    };
    const nodeSelectHandler = (e: React.MouseEvent, node: ComposerAttributeNode) => {
        const ctrlKey = e.ctrlKey || e.metaKey;
        const shiftKey = e.shiftKey;

        // Funzione helper per ottenere l'attributo aggiornato dallo store
        const getAttributeFromStore = (nodeId: string): ComposerAttributeNode | undefined => {
            const attr = currentAttributes.find((attr) => attr.id === nodeId);
            return attr;
        };

        if (shiftKey && selectedIds.length > 0) {
            const lastSelectedId = selectedIds[selectedIds.length - 1];
            const lastSelectedNode = findNodeById(lastSelectedId);
            if (!lastSelectedNode) return;

            const siblings = filteredData.filter((n) => n.parentID === lastSelectedNode.parentID);
            const startIndex = siblings.findIndex((n) => n.id === lastSelectedNode.id);
            const endIndex = siblings.findIndex((n) => n.id === node.id);

            if (startIndex < 0 || endIndex < 0) return;

            const range = siblings.slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1);
            // Prendi gli attributi aggiornati dallo store
            const rangeFromStore = range.map((n) => getAttributeFromStore(n.id)).filter(Boolean) as ComposerAttributeNode[];
            setSelectedIds(range.map((n) => n.id));
            selectAttributes(rangeFromStore);
            selectOptions(null);
            return;
        }
        // con control key, aggiungo o rimuovo l'elemento selezionato
        if (ctrlKey && selectedIds.includes(node.id)) {
            setSelectedIds(selectedIds.filter((id) => id !== node.id));
            selectAttributes(selectedAttributes?.filter((attr) => attr.id !== node.id) || []);
            // se rimane 1 attributo selezionato, seleziono la sua prima opzione
            if (selectedAttributes?.length === 1) {
                const attrFromStore = getAttributeFromStore(selectedAttributes[0].id);
                if (attrFromStore && attrFromStore.options) {
                    selectOptions(attrFromStore.options ? [attrFromStore.options[0]] : []);
                }
            } else {
                selectOptions([]);
            }
            return;
            // altrimenti sto selezionando un nuovo nodo
        } else if (ctrlKey) {
            const nodeFromStore = getAttributeFromStore(node.id);
            if (!nodeFromStore) return;
            setSelectedIds((prev) => [...prev, node.id]);
            selectAttributes([...(selectedAttributes ?? []), nodeFromStore]);
            selectOptions(null);
            return;
        }
        // se non c'è control key, seleziono solo l'elemento corrente
        // IMPORTANTE: Prendi l'attributo aggiornato dallo store, non dal tree state!
        const nodeFromStore = getAttributeFromStore(node.id);
        setSelectedIds([node.id]);
        selectAttributes(nodeFromStore ? [nodeFromStore] : []);
        if (!nodeFromStore || !nodeFromStore.options || nodeFromStore.options.length === 0) {
            selectOptions([]);
            return;
        }
        selectOptions(nodeFromStore?.options ? [nodeFromStore.options[0]] : []);
    };

    const moveNodeHandler = (node: ComposerAttributeNode, direction: 'up' | 'down') => {
        const siblings = filteredData
            .filter((n) => n.parentID === node.parentID)
            .sort((a, b) =>
                sortByDisplay(
                    filteredData.filter((n) => n.parentID === node.parentID),
                    a,
                    b
                )
            );

        const currentIndex = siblings.findIndex((n) => n.id === node.id);
        const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (swapIndex >= 0 && swapIndex < siblings.length) {
            const swapNode = siblings[swapIndex];
            const currentOrder = node.displayOrder;

            updateNode(node.id, { ...node, displayOrder: swapNode.displayOrder });
            updateNode(swapNode.id, { ...swapNode, displayOrder: currentOrder });
            updateAttribute({ ...node, displayOrder: swapNode.displayOrder });
            updateAttribute({ ...swapNode, displayOrder: currentOrder });
            validateAttributes();
        }
    };

    const dragStartHandler = (e: React.DragEvent<HTMLDivElement>, node: ComposerAttributeNode) => {
        // Allow both Attribute and AttributeStep types to be dragged
        if (node.type === ComposerAttributeType.Attribute || node.type === ComposerAttributeType.AttributeStep) {
            e.dataTransfer.setData('text/plain', node.id);
            e.dataTransfer.effectAllowed = 'move';
            // Store node type to know what we're dragging
            e.dataTransfer.setData('node-type', node.type.toString());
        } else {
            e.preventDefault();
        }
    };

    const dragOverHandler = (e: React.DragEvent<HTMLDivElement>, node: ComposerAttributeNode) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const dropHandler = (e: React.DragEvent<HTMLDivElement>, node: ComposerAttributeNode | null) => {
        e.preventDefault();
        const draggedNodeId = e.dataTransfer.getData('text/plain');
        const draggedNodeType = e.dataTransfer.getData('node-type');
        const draggedNode = findNodeById(draggedNodeId);

        if (!draggedNode) return;

        // Handle dropping into the root (no target node)
        if (!node) {
            if (draggedNode.parentID) {
                updateNode(draggedNodeId, { ...draggedNode, parentID: undefined, parentIDNumeric: -1, parentType: undefined, displayOrder: -1 });
                updateAttribute({ ...draggedNode, parentID: undefined, parentIDNumeric: -1, parentType: undefined, displayOrder: -1 });
                validateAttributes();
            }
            return;
        }

        // Prevent dropping a node on itself
        if (node.id === draggedNode.id) return;

        // Prevent dropping a node into its own descendant
        let current = node;
        while (current.parentID) {
            if (current.parentID === draggedNode.id) return;
            const parentNode = findNodeById(current.parentID);
            if (!parentNode) break;
            current = parentNode;
        }

        // Special case: Step being dropped into a Group
        if (draggedNode.type === ComposerAttributeType.AttributeStep && node.type === ComposerAttributeType.AttributeGroup) {
            // Change parent to the group
            const children = getNodeChildren(node);
            updateNode(draggedNodeId, { ...draggedNode, parentID: node.id, parentType: node.type, displayOrder: children.length });
            updateAttribute({ ...draggedNode, parentID: node.id, parentIDNumeric: node.idNumeric, parentType: node.type, displayOrder: children.length });
            validateAttributes();
            return;
        }

        // If the target node can be a parent (not an Attribute type)
        if (node.type !== ComposerAttributeType.Attribute) {
            // Change parent and move to the end of the new children list
            if (draggedNode.parentID !== node.id) {
                const children = getNodeChildren(node);
                updateNode(draggedNodeId, { ...draggedNode, parentID: node.id, parentType: node.type, displayOrder: children.length });
                updateAttribute({ ...draggedNode, parentID: node.id, parentIDNumeric: node.idNumeric, parentType: node.type, displayOrder: children.length });
                validateAttributes();
            }
            return;
        }

        // Reordering within the same parent or moving to a new parent (dropping next to a sibling)
        const newParentId = node.parentID;
        const newParentIdNumeric = node.parentIDNumeric;
        if (draggedNode.parentID === newParentId) {
            // Reordering within the same parent
            const siblings = filteredData.filter((n) => n.parentID === newParentId).sort((a, b) => sortByDisplay(filteredData, a, b));
            const draggedIndex = siblings.findIndex((n) => n.id === draggedNode.id);
            const dropIndex = siblings.findIndex((n) => n.id === node.id);

            if (draggedIndex === dropIndex) return;

            const newSiblings = [...siblings];
            const [movedItem] = newSiblings.splice(draggedIndex, 1);
            newSiblings.splice(dropIndex, 0, movedItem);

            newSiblings.forEach((sibling: ComposerAttributeNode, index: number) => {
                if (sibling.displayOrder !== index) {
                    updateNode(sibling.id, { ...sibling, displayOrder: index });
                    updateAttribute({ ...sibling, displayOrder: index });
                }
            });
        } else {
            // Moving node to a new parent (next to a new sibling)
            updateNode(draggedNodeId, { ...draggedNode, parentID: newParentId, parentIDNumeric: newParentIdNumeric, parentType: node.type });
            updateAttribute({ ...draggedNode, parentID: newParentId, parentIDNumeric: newParentIdNumeric, parentType: node.type });

            // Re-calculate display order for both old and new siblings
            const oldSiblings = filteredData
                .filter((n) => n.parentID === draggedNode.parentID && n.id !== draggedNode.id)
                .sort((a, b) => sortByDisplay(filteredData, a, b));
            oldSiblings.forEach((sibling, index) => {
                if (sibling.displayOrder !== index) {
                    updateNode(sibling.id, { ...sibling, displayOrder: index });
                    updateAttribute({ ...sibling, displayOrder: index });
                }
            });

            const newSiblings = [...filteredData.filter((n) => n.parentID === newParentId), draggedNode].sort((a, b) => sortByDisplay(filteredData, a, b));
            const dropIndex = newSiblings.findIndex((n) => n.id === node.id);
            const [movedItem] = newSiblings.splice(
                newSiblings.findIndex((s) => s.id === draggedNode.id),
                1
            );
            newSiblings.splice(dropIndex, 0, movedItem);

            newSiblings.forEach((sibling, index) => {
                if (sibling.displayOrder !== index || (sibling.id === draggedNode.id && sibling.parentID !== newParentId)) {
                    updateNode(sibling.id, {
                        ...sibling,
                        parentID: newParentId,
                        parentIDNumeric: newParentIdNumeric,
                        parentType: node.type,
                        displayOrder: index
                    });
                    updateAttribute({ ...sibling, parentID: newParentId, parentIDNumeric: newParentIdNumeric, parentType: node.type, displayOrder: index });
                }
            });
        }
        validateAttributes();
    };

    // Removed problematic useEffect that was causing "Rendered fewer hooks" error
    // This was auto-selecting first attribute but interfered with manual selection logic

    const nodeSubElements = (node: ComposerAttributeNode, level: number = 1) => {
        const children = getNodeChildren(node);
        const sortedChildren = children.sort((a, b) => sortByDisplay(children, a, b));
        return sortedChildren.map((child) => (
            <ComposerAttributeSubTreeElement
                key={child.id}
                attribute={child}
                hasLink={child.links && child.links.length > 0}
                selected={selectedIds.includes(child.id)}
                onSelect={nodeSelectHandler}
                shift={level}
                onMoveDown={(child) => {
                    moveNodeHandler(child, 'down');
                }}
                onMoveUp={(child) => {
                    moveNodeHandler(child, 'up');
                }}
                onDragStart={(e) => dragStartHandler(e, child)}
                onDragOver={(e) => dragOverHandler(e, child)}
                onDrop={(e) => dropHandler(e, child)}
                onDoubleClick={() => {
                    editAttributeHandler(child);
                }}
            />
        ));
    };

    React.useEffect(() => {
        //TODO fix this hack with a proper state management
        setTimeout(() => {
            setSelectedIds([]);
            selectAttributes(null);
            console.log('ids', selectedIds);
        }, 0);
    }, []);

    return (
        <ComposerAttributesContainer>
            <ComposerAttributesHeader>
                <ToolGrid tools={buttons} columns={4} />
            </ComposerAttributesHeader>
            <ComposerAttributesBody>
                <ComposerAttributesList>
                    <ComposerAttributesOverlay
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            dropHandler(e, null);
                        }}
                    />
                    <ComposerAttributesSelector
                        selected={selectedIds.length === filteredData.length && filteredData.length > 0 && (!selectedOptions || selectedOptions.length === 0)}
                        onClick={() => {
                            // If all attributes are selected, deselect all
                            if (selectedIds.length === filteredData.length) {
                                setSelectedIds([]);
                                selectAttributes(null);
                            } else {
                                // Otherwise, select all attributes
                                setSelectedIds(filteredData.map((attr) => attr.id));
                                selectAttributes(nodes);
                            }
                        }}
                    />
                    <ComposerAttributesCollapseSelector
                        onExpand={() => {
                            nodes.forEach((node) => {
                                node.expanded = false;
                                updateNode(node.id, { ...node });
                            });
                        }}
                        onCollapse={() => {
                            nodes.forEach((node) => {
                                node.expanded = true;
                                updateNode(node.id, { ...node });
                            });
                        }}
                    />
                    {nodes.map((node) => {
                        const children = getNodeChildren(node);
                        const sortedChildren = children.sort((a, b) => sortByDisplay(children, a, b));

                        return (
                            <ComposerAttributeTreeElement
                                key={node.id}
                                attribute={node}
                                selected={selectedIds.includes(node.id)}
                                type={node.type}
                                hasLink={node.links && node.links.length > 0}
                                onSelect={nodeSelectHandler}
                                onDragStart={(e) => dragStartHandler(e, node)}
                                onDragOver={(e) => dragOverHandler(e, node)}
                                onDrop={(e) => dropHandler(e, node)}
                                expanded={node.expanded}
                                onMoveDown={(node) => {
                                    moveNodeHandler(node, 'down');
                                }}
                                onMoveUp={(node) => {
                                    moveNodeHandler(node, 'up');
                                }}
                                onDoubleClick={() => {
                                    editAttributeHandler(node);
                                }}
                                onToggleExpand={(node) => {
                                    node.expanded = !node.expanded;
                                    updateNode(node.id, {
                                        ...node
                                    });
                                }}
                            >
                                {node.type !== ComposerAttributeType.Attribute &&
                                    sortedChildren.map((child) => {
                                        if (child.type === ComposerAttributeType.AttributeStep && !!child.parentID) {
                                            return (
                                                <ComposerAttributeTreeElement
                                                    key={child.id}
                                                    attribute={child}
                                                    selected={selectedIds.includes(child.id)}
                                                    type={child.type}
                                                    shift={1}
                                                    hasLink={child.links && child.links.length > 0}
                                                    onSelect={nodeSelectHandler}
                                                    onDragStart={(e) => dragStartHandler(e, child)}
                                                    onDragOver={(e) => dragOverHandler(e, child)}
                                                    onDrop={(e) => dropHandler(e, child)}
                                                    expanded={child.expanded}
                                                    onMoveDown={(child) => {
                                                        moveNodeHandler(child, 'down');
                                                    }}
                                                    onMoveUp={(child) => {
                                                        moveNodeHandler(child, 'up');
                                                    }}
                                                    onDoubleClick={() => {
                                                        editAttributeHandler(child);
                                                    }}
                                                    onToggleExpand={(child) => {
                                                        child.expanded = !child.expanded;
                                                        updateNode(child.id, {
                                                            ...child
                                                        });
                                                    }}
                                                >
                                                    {nodeSubElements(child, 2)}
                                                </ComposerAttributeTreeElement>
                                            );
                                        }

                                        return nodeSubElements(node);
                                    })}
                            </ComposerAttributeTreeElement>
                        );
                    })}
                </ComposerAttributesList>
            </ComposerAttributesBody>
        </ComposerAttributesContainer>
    );
};

export default ComposerAttributes;
